import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { type AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance } from '@/auth/msal'
import { ROLE_IDS, type RoleName } from '@/auth/roles.ts'

type TokenClaims = {
  roles?: string[]
  groups?: string[]
}

export const useAuthStore = defineStore('authStore', () => {
  const account = ref<AccountInfo | null>(null)
  const error = ref('')
  const roleIds = ref<string[]>([])

  /**
   * Get MSAL Account
   */
  async function refreshRoles() {
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length === 0) {
      roleIds.value = []
      account.value = null
      return
    }

    const activeAccount = msalInstance.getActiveAccount() ?? accounts[0]
    msalInstance.setActiveAccount(activeAccount)

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent({
        account: activeAccount,
        scopes: ['openid', 'profile'],
      })

      account.value = {
        ...tokenResponse.account,
        idTokenClaims: tokenResponse.idTokenClaims as TokenClaims,
      }

      setRolesFromClaims(tokenResponse.idTokenClaims as TokenClaims)
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect({
          account: activeAccount,
          scopes: ['openid', 'profile'],
        })
        return
      }

      error.value = e instanceof Error ? e.message : String(e)
      throw e
    }
  }

  async function signOut() {
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length === 0) {
      return
    }

    const logoutRequest = {
      account: msalInstance.getAccount({ username: accounts[0].username }),
      postLogoutRedirectUri: '/signout',
    }

    await msalInstance.logoutRedirect(logoutRequest)
  }

  function setRolesFromClaims(claims?: TokenClaims) {
    roleIds.value = claims?.roles ?? claims?.groups ?? []
  }

  function hasRole(role: RoleName): boolean {
    return roleIds.value.includes(ROLE_IDS[role])
  }

  function hasAnyRole(roles: RoleName[]): boolean {
    return roles.some((role) => hasRole(role))
  }

  function hasAllRoles(roles: RoleName[]): boolean {
    return roles.every((role) => hasRole(role))
  }

  const readableRoles = computed(() =>
    (Object.keys(ROLE_IDS) as RoleName[]).filter((role) => roleIds.value.includes(ROLE_IDS[role])),
  )

  /**
   * Escape hatch when msal gets stuck
   */
  function reset() {
    sessionStorage.clear()
    account.value = null
    error.value = ''
  }

  /**
   * Gets the current MSAL Token
   */
  async function getToken(claims?: string) {
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length === 0) {
      throw new Error('No account available')
    }

    const activeAccount = msalInstance.getActiveAccount() ?? accounts[0]
    msalInstance.setActiveAccount(activeAccount)

    const tokenRequest = {
      scopes: [import.meta.env.VITE_ENTRA_ID_SCOPE],
      account: activeAccount,
      claims,
    }

    if (claims) {
      await msalInstance.acquireTokenRedirect(tokenRequest)
      throw new Error('Redirecting for step-up authentication')
    }

    try {
      const result = await msalInstance.acquireTokenSilent(tokenRequest)
      return result.accessToken
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect(tokenRequest)
        throw new Error('Redirecting for authentication')
      }

      throw e
    }
  }

  return {
    // State
    account,
    error,
    roleIds,

    // Actions
    refreshRoles,
    signOut,
    reset,
    getToken,
    readableRoles,
    hasRole,
    hasAnyRole,
    hasAllRoles,
  }
})
